import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Box, Typography, List, ListItem, ListItemText, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { publicInstance } from '../axiosInstance'; // Dostosuj ścieżkę importu w razie potrzeby
import { format } from 'date-fns'; // Dodaj ten import do formatowania daty
import { useSpring, animated } from '@react-spring/web';

interface SnakePopupProps {
  open: boolean;
  onClose: () => void;
}

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
type Position = { x: number; y: number };

interface LeaderboardEntry {
  username: string;
  gname: string;
  score: number;
  time_stamp: string;
}

// Dodaj nowy interfejs dla gier
interface Game {
  id: string;
  name: string;
  description: string;
}

const AVAILABLE_GAMES: Game[] = [
  { id: 'snake', name: 'Snake', description: 'Classic snake game' },
  { id: 'rps', name: 'Rock Paper Scissors', description: 'Play against AI!' },
  { id: 'pong', name: 'Pong', description: 'Coming soon...' },
];

const GRID_SIZE = 30;
const CELL_SIZE = 18;
const INITIAL_SPEED = 100;
const SPEED_INCREASE = 2;
const SPEED_INCREASE_INTERVAL = 2;

const SnakePopup: React.FC<SnakePopupProps> = ({ open, onClose }) => {
  const [snake, setSnake] = useState<Position[]>([
    { x: 10, y: 10 }, // Head
    { x: 9, y: 10 }   // Initial tail segment
  ]);
  const [food, setFood] = useState<Position>({ x: 5, y: 5 });
  const [direction, setDirection] = useState<Direction>('RIGHT');
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]); // Stan leaderboardu
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<string>('snake');
  const [isUpdating, setIsUpdating] = useState(false); // Dodaj nowy stan
  const [personalBest, setPersonalBest] = useState<number>(0);
  const [showNewRecord, setShowNewRecord] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [nextDirection, setNextDirection] = useState<Direction | null>(null);
  const [lastValidDirection, setLastValidDirection] = useState<Direction>(direction);
  const [nextMove, setNextMove] = useState<Direction | null>(null);
  const [directionQueue, setDirectionQueue] = useState<Direction[]>([]);
  const [showMaxSpeedAlert, setShowMaxSpeedAlert] = useState(false);
  const [rpsChoice, setRpsChoice] = useState<string | null>(null);
  const [computerChoice, setComputerChoice] = useState<string | null>(null);
  const [rpsResult, setRpsResult] = useState<string | null>(null);
  const [rpsScore, setRpsScore] = useState(0);

  const generateFood = useCallback(() => {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * (GRID_SIZE - 2)) + 1,
        y: Math.floor(Math.random() * (GRID_SIZE - 2)) + 1,
      };
      // Check if the new food position overlaps with any snake segment
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    setFood(newFood);
  }, [snake]);

  const resetGame = useCallback(() => {
    setSnake([
      { x: 10, y: 10 }, // Head
      { x: 9, y: 10 }   // Initial tail segment
    ]);
    setDirection('RIGHT');
    setScore(0);
    setSpeed(INITIAL_SPEED);
    setShowNewRecord(false);
    setScoreSaved(false);
    generateFood();
  }, [generateFood]);

  const startNewGame = useCallback(() => {

    setGameStarted(false);
    setGameOver(false);
    setScoreSaved(false);
  }, []);

  // Pobieranie danych leaderboardu
  const fetchLeaderboard = useCallback(async () => {
    if (isUpdating) return;

    setLoading(true);
    setError(null);
    try {
      const response = await publicInstance.get('/dacapi_user_score');
      const data: LeaderboardEntry[] = response.data;
      setLeaderboard(data); // Store all scores

      // Set personal best for current game
      const username = localStorage.getItem('username');
      const userBest = data.find(entry => 
        entry.username === username && entry.gname === selectedGame
      );
      if (userBest) {
        setPersonalBest(userBest.score);
      }
    } catch (err) {
      setError('Nie udało się pobrać leaderboardu.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isUpdating, selectedGame]);

  useEffect(() => {
    if (open) {
      fetchLeaderboard();
    }
  }, [open, fetchLeaderboard]);

  // Zmodyfikowana obsługa klawiszy
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (gameOver || !gameStarted) return;

      e.preventDefault();

      const newDirection = (() => {
        switch (e.code) {
          case 'ArrowUp':
          case 'KeyW': return 'UP';
          case 'ArrowDown':
          case 'KeyS': return 'DOWN';
          case 'ArrowLeft':
          case 'KeyA': return 'LEFT';
          case 'ArrowRight':
          case 'KeyD': return 'RIGHT';
          default: return null;
        }
      })();

      if (!newDirection) return;

      setDirectionQueue(prevQueue => {
        // Opcjonalnie: Limitujemy rozmiar kolejki, aby nie rosła w nieskończoność
        if (prevQueue.length >= 5) {
          return prevQueue;
        }
        return [...prevQueue, newDirection];
      });
    };

    window.addEventListener('keydown', handleKeyPress, { passive: false });
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameOver, gameStarted]);


  // Uproszczona wersja bez bufora następnego ruchu
  const isValidMove = (currentDir: Direction, newDir: Direction): boolean => {
    return !(
      (currentDir === 'UP' && newDir === 'DOWN') ||
      (currentDir === 'DOWN' && newDir === 'UP') ||
      (currentDir === 'LEFT' && newDir === 'RIGHT') ||
      (currentDir === 'RIGHT' && newDir === 'LEFT')
    );
  };


  // Zmodyfikuj efekt ruchu węża
  useEffect(() => {
    if (gameOver || !open || !gameStarted) return;

    const moveSnake = () => {
      let newDirection = direction;

      // Przetwarzamy jeden kierunek z kolejki
      if (directionQueue.length > 0) {
        const nextDir = directionQueue[0];
        if (isValidMove(newDirection, nextDir)) {
          newDirection = nextDir;
          setDirection(newDirection);
        }
        // Usuwamy kierunek z kolejki niezależnie od tego, czy był poprawny
        setDirectionQueue(prevQueue => prevQueue.slice(1));
      }

      setSnake(currentSnake => {
        const head = currentSnake[0];
        const newHead = { ...head };

        switch (newDirection) {
          case 'UP':
            newHead.y -= 1;
            break;
          case 'DOWN':
            newHead.y += 1;
            break;
          case 'LEFT':
            newHead.x -= 1;
            break;
          case 'RIGHT':
            newHead.x += 1;
            break;
        }

        const newSnake = [newHead, ...currentSnake];

        // Sprawdzanie kolizji ze ścianami
        const hitWall = newHead.x < 0 || newHead.x >= GRID_SIZE || newHead.y < 0 || newHead.y >= GRID_SIZE;

        // Sprawdzanie kolizji z samym sobą
        const hitSelf = currentSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y);

        if (hitWall || hitSelf) {
          setGameOver(true);
        }

        // Sprawdzenie, czy jedzenie zostało zjedzone
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(prevScore => {
            const newScore = prevScore + 1;
            if (newScore % SPEED_INCREASE_INTERVAL === 0) {
              setSpeed(prevSpeed => {
                const newSpeed = Math.max(prevSpeed - SPEED_INCREASE, 50);
                if (newSpeed === 50 && prevSpeed !== 50) {
                  setShowMaxSpeedAlert(true);
                  setTimeout(() => setShowMaxSpeedAlert(false), 2000); // Ukryj alert po 2 sekundach
                }
                return newSpeed;
              });
            }
            return newScore;
          });
          generateFood();
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    };

    const gameInterval = setInterval(moveSnake, speed);
    return () => clearInterval(gameInterval);
  }, [direction, directionQueue, food, gameOver, generateFood, open, gameStarted, speed]);



  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      setGameStarted(true);
      setCountdown(null);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev! - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // Funkcja do zapisywania wyniku
  const saveScore = useCallback(async (finalScore: number) => {
    if (isUpdating) return; // Zabezpieczenie przed wielokrotnym zapisem

    const username = localStorage.getItem('username');
    if (!username) {
      console.error('No username found in localStorage');
      return;
    }

    setIsUpdating(true); // Rozpoczęcie aktualizacji
    const currentTimestamp = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx");

    try {
      // Sprawdź czy gracz już istnieje w tabeli
      const existingPlayer = leaderboard.find(entry =>
        entry.username === username && entry.gname === 'snake'
      );

      if (!existingPlayer) {
        // Jeśli gracz nie istnieje, dodaj nowy rekord
        await publicInstance.post('/dacapi_user_score', {
          username,
          gname: 'snake',
          score: finalScore,
          time_stamp: currentTimestamp
        });
        setShowNewRecord(true);
      } else if (finalScore > existingPlayer.score) {
        // Jeśli gracz istnieje i ma lepszy wynik, zaktualizuj rekord
        await publicInstance.patch(
          `/dacapi_user_score?username=eq.${username}`,
          {
            score: finalScore,
            time_stamp: currentTimestamp
          }
        );
        setShowNewRecord(true);
      }

      // Odśwież leaderboard tylko raz po zapisaniu wyniku
      await fetchLeaderboard();
    } catch (error) {
      console.error('Error saving score:', error);
      setError('Nie udało się zapisać wyniku.');
    } finally {
      setIsUpdating(false); // Zakończenie aktualizacji
    }
  }, [leaderboard, fetchLeaderboard, isUpdating]);

  // Efekt dla game over
  useEffect(() => {
    if (gameOver && !scoreSaved) {

      setScoreSaved(true);
      saveScore(score).then(() => {

      });
    }
  }, [gameOver, score, saveScore, scoreSaved]);

  // Efekt dla początkowego załadowania
  useEffect(() => {
    if (open && !isUpdating) {
      fetchLeaderboard();
    }
  }, [open, fetchLeaderboard]);

  // Zmodyfikowany render leaderboardu
  const renderLeaderboard = () => {
    if (loading && !leaderboard.length) {
      return <Typography>Ładowanie...</Typography>;
    }

    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    // Filter leaderboard based on selected game and sort by score in descending order
    const filteredLeaderboard = leaderboard
      .filter(entry => entry.gname === selectedGame)
      .sort((a, b) => b.score - a.score);
    
    return (
      <List>
        {filteredLeaderboard.map((entry, index) => (
          <ListItem
            key={`${entry.username}-${entry.score}-${entry.time_stamp}`}
            sx={{
              bgcolor: index % 2 === 0 ? '#3c3c3c' : '#2c2c2c',
              borderRadius: 1,
              mb: 1
            }}
          >
            <ListItemText
              primary={`${index + 1}. ${entry.username}`}
              secondary={`Wynik: ${entry.score}`}
            />
          </ListItem>
        ))}
      </List>
    );
  };



  useEffect(() => {

    return () => {

    };
  }, []);

  const newRecordAnimation = useSpring({
    from: {
      opacity: 0,
      transform: 'scale(0.5) translateY(-50px)',
    },
    to: {
      opacity: showNewRecord ? 1 : 0,
      transform: showNewRecord ? 'scale(1) translateY(0)' : 'scale(0.5) translateY(-50px)',
    },
    config: {
      tension: 300,
      friction: 20,
    },
  });

  const playRPS = (playerChoice: string) => {
    const choices = ['rock', 'paper', 'scissors'];
    const computerChoice = choices[Math.floor(Math.random() * choices.length)];
    setRpsChoice(playerChoice);
    setComputerChoice(computerChoice);

    if (playerChoice === computerChoice) {
      setRpsResult('Draw!');
    } else if (
      (playerChoice === 'rock' && computerChoice === 'scissors') ||
      (playerChoice === 'paper' && computerChoice === 'rock') ||
      (playerChoice === 'scissors' && computerChoice === 'paper')
    ) {
      setRpsResult('You win!');
      setRpsScore(prev => prev + 1);
    } else {
      setRpsResult('Computer wins!');
    }
  };

  const renderContent = () => {
    // Render RPS game if selected
    if (selectedGame === 'rps') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 4 }}>
            Rock Paper Scissors
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Score: {rpsScore}
          </Typography>
          
          {rpsChoice && computerChoice && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">
                Your choice: {rpsChoice}
              </Typography>
              <Typography variant="h6">
                Computer's choice: {computerChoice}
              </Typography>
              <Typography variant="h5" sx={{ color: '#4CAF50', mt: 2 }}>
                {rpsResult}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {['rock', 'paper', 'scissors'].map((choice) => (
              <Box
                key={choice}
                onClick={() => playRPS(choice)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: '#4CAF50',
                  color: 'white',
                  padding: 2,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#45a049'
                  }
                }}
              >
                {choice.toUpperCase()}
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    // Render Snake game content
    if (selectedGame === 'snake') {
      if (gameOver) {
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Score: {score}
            </Typography>
            <Typography variant="h5" color="error" sx={{ mb: 3 }}>
              Game Over!
            </Typography>

            {showNewRecord && (
              <animated.div style={newRecordAnimation}>
                <Typography
                  variant="h3"
                  sx={{
                    color: '#4CAF50',
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    mb: 3,
                    background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    padding: '20px',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(76, 175, 80, 0.1)',
                      borderRadius: '10px',
                      zIndex: -1,
                    }
                  }}
                >
                  NEW HIGH SCORE!
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#66bb6a',
                    mb: 3,
                    fontStyle: 'italic'
                  }}
                >
                  Congratulations!
                </Typography>
              </animated.div>
            )}

            <Typography
              sx={{
                cursor: 'pointer',
                color: '#4CAF50',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                mt: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  textDecoration: 'underline',
                  transform: 'scale(1.05)',
                  color: '#45a049'
                }
              }}
              onClick={() => {

                startNewGame();
              }}
            >
              Play Again
            </Typography>
          </Box>
        );
      }

      if (!gameStarted) {
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
              Welcome to Snake Game v2
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 3, color: 'grey.600' }}>
              New: Speed limit added to improve collision detection!
            </Typography>
            {countdown !== null ? (
              <Typography variant="h2" sx={{ color: '#4CAF50' }}>
                {countdown}
              </Typography>
            ) : (
              <Typography
                sx={{
                  cursor: 'pointer',
                  color: '#4CAF50',
                  fontSize: '1.5rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#45a049'
                  }
                }}
                onClick={() => {

                  resetGame();
                  setCountdown(3);
                }}
              >
                Start Game
              </Typography>
            )}
          </Box>
        );
      }

      return (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Score: {score}
            <Typography variant="subtitle1" component="span" sx={{ ml: 2, color: 'grey.500' }}>
              Speed: {Math.min(Math.floor(score / SPEED_INCREASE_INTERVAL), 25)}x
            </Typography>
          </Typography>
          {showMaxSpeedAlert && (
            <Alert 
              severity="info" 
              sx={{ 
                position: 'absolute', 
                top: '20px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 1000,
                backgroundColor: '#2196f3',
                color: 'white'
              }}
            >
              Maximum speed reached!
            </Alert>
          )}
          <Box
            sx={{
              width: GRID_SIZE * CELL_SIZE,
              height: GRID_SIZE * CELL_SIZE,
              border: '2px solid #333',
              position: 'relative',
              bgcolor: '#001500',
              borderRadius: '8px',
              boxShadow: '0 0 20px rgba(0,0,0,0.3)'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: CELL_SIZE - 4,
                height: CELL_SIZE - 4,
                left: food.x * CELL_SIZE + 2,
                top: food.y * CELL_SIZE + 2,
                background: 'radial-gradient(circle, #ff5252 40%, #ff1744 100%)',
                borderRadius: '50%',
                boxShadow: '0 0 8px #ff1744',
                animation: 'pulse 1.5s infinite'
              }}
            />
            {snake.map((segment, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  width: CELL_SIZE - 2,
                  height: CELL_SIZE - 2,
                  left: segment.x * CELL_SIZE + 1,
                  top: segment.y * CELL_SIZE + 1,
                  bgcolor: index === 0
                    ? '#69f0ae'
                    : '#4CAF50',
                  borderRadius: index === 0 ? '6px' : '4px',
                  border: index === 0
                    ? '2px solid #00e676'
                    : '1px solid #43a047',
                  boxSizing: 'border-box',
                  transform: index === 0 ? 'scale(1.1)' : 'scale(1)',
                  zIndex: index === 0 ? 2 : 1,
                  transition: 'left 0.08s linear, top 0.08s linear'// Bardzo krótka animacja
                }}
              />
            ))}
          </Box>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Controls</Typography>
            <Typography>Use arrow keys to move the snake</Typography>
          </Box>
        </>
      );
    }

    // Render placeholder for other games
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4">
          Coming Soon...
        </Typography>
      </Box>
    );
  };

  // Add effect to reset game state when changing games
  useEffect(() => {
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setRpsScore(0);
    setRpsChoice(null);
    setComputerChoice(null);
    setRpsResult(null);
  }, [selectedGame]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
    >
      <Box sx={{
        bgcolor: '#1a1a1a',
        height: '100vh',
        color: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Snake Game (Alfa)
            <IconButton onClick={onClose} size="large" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ flexGrow: 1 }}>
          <Box sx={{
            display: 'flex',
            height: '100%',
            width: '100%'
          }}>
            {/* Sekcja Leaderboard */}
            <Box sx={{
              width: 300,
              bgcolor: '#2c2c2c',
              padding: 2,
              overflowY: 'auto'
            }}>
              <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
                Leaderboard
              </Typography>
              {renderLeaderboard()}
            </Box>
            {/* Sekcja Gry - Wyśrodkowana */}
            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2
              }}>
                {renderContent()}
              </Box>
            </Box>

            {/* Menu gier po prawej */}
            <Box sx={{
              width: 300,
              bgcolor: '#2c2c2c',
              padding: 2,
              overflowY: 'auto'
            }}>
              <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
                Games Menu
              </Typography>
              <List>
                {AVAILABLE_GAMES.map((game) => (
                  <ListItem
                    key={game.id}
                    sx={{
                      bgcolor: selectedGame === game.id ? '#4CAF50' : '#3c3c3c',
                      borderRadius: 1,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: selectedGame === game.id ? '#45a049' : '#4c4c4c'
                      }
                    }}
                    onClick={() => setSelectedGame(game.id)}
                  >
                    <ListItemText
                      primary={game.name}
                      secondary={
                        <Typography
                          component="span"
                          sx={{
                            color: selectedGame === game.id ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'
                          }}
                        >
                          {game.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default SnakePopup;